import { QueryCache, MutationCache, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import * as Sentry from '@sentry/react'
import { ReactNode } from 'react'
import { pb } from '../lib/pocketbase'
import { clearSignedInDeviceData } from '../utils/deviceData'

function shouldReport(error: unknown): boolean {
  const status = (error as any)?.status || 0
  // 4xx = expected (no permission, not found, bad input) — not infrastructure bugs
  return !(status >= 400 && status < 500)
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!shouldReport(error)) return
      Sentry.captureException(error, {
        extra: { queryKey: query.queryKey },
        tags: { area: 'query' },
      })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (!shouldReport(error)) return
      Sentry.captureException(error, {
        tags: { area: 'mutation' },
      })
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // data fresh for 5 min — no refetch within this window
      gcTime: 24 * 60 * 60 * 1000,   // keep in memory 24h so persisted cache survives tab close
      retry: (failureCount, error) => {
        const status = (error as any)?.status
        if (status >= 400 && status < 500) return false
        return failureCount < 2
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10_000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
})

// Persist the query cache to localStorage so users see instant data on
// page refresh or tab reopen instead of a loading spinner every time.
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'reppr-query-cache',
})

/**
 * The cache dies with the session.
 *
 * Without this, a session that ends leaves its pages behind in localStorage
 * under the key above. That matters because a dead PocketBase token does not
 * produce errors to discard — record rules match nothing, so every list comes
 * back 200 and empty, and those empty pages are what gets persisted. Sign in
 * again and they are served straight back: the desk and the money page read
 * $0, no clients and no contracts while the API is returning the real book.
 * Observed Sep 2026 — the API answered with 6 contracts while the page showed
 * 0, and clearing this key alone fixed it.
 *
 * It also keeps one person's book from being read by the next person to sign
 * in on the same browser. Clearing on sign-out alone did not quite do that: a
 * session that simply expired was never cleared, so the next person to sign
 * in on that page could be shown the last one's cached pages until they
 * refetched (Oct 2026 audit). So the cache belongs to one user id: a sign-in
 * as anyone else discards it first, and a cache saved for someone else is
 * never restored (the persister's buster is the user id).
 */
const OWNER_KEY = 'reppr-query-cache-owner'
const owner = () => { try { return localStorage.getItem(OWNER_KEY) } catch { return null } }

pb.authStore.onChange((token, model) => {
  const id = token ? (model?.id ?? null) : null
  if (id && id === owner()) return
  queryClient.clear()
  persister.removeClient()
  try {
    if (id) localStorage.setItem(OWNER_KEY, id)
    else localStorage.removeItem(OWNER_KEY)
  } catch (err) {
    console.error('[cache] could not record whose cache this is:', err)
  }
  if (!token) clearSignedInDeviceData()
})

export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // cache survives up to 24 hours
        // A cache persisted for another user (or for nobody) is discarded on load.
        buster: pb.authStore.model?.id ?? 'signed-out',
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}

export { queryClient }
